/* eslint-disable import/order */
import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem, 
  Stack,
  Typography,
  Alert,
  CircularProgress,
  Box,
} from '@mui/material';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs, { Dayjs } from 'dayjs';
import 'dayjs/locale/es';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import { useTranslation } from 'react-i18next';
import { useSnackbar } from 'notistack';

import { Reunion, ReunionCanal } from '../../types/reuniones';
import { useAgendarSiguienteReunion, getServicioNombre } from '../../hooks/useReuniones';
import { useUsuarios, Usuario as UsuarioFromHook } from '../../hooks/useUsuarios';
import { REUNION_CANAL_VALUES } from '../../utils/reuniones';
import { REUNION_ESTADO_VALUES, getEstadoDisplayLabel } from '../../utils/reuniones';
import { useUpdateReunionEstado } from '../../hooks/useReuniones';

dayjs.extend(utc);
dayjs.extend(timezone);
const TZ = 'Europe/Madrid';

interface Props {
  open: boolean;
  reunion: Reunion | null;
  fechaSugerida: Date | string | null;
  onClose: () => void;
  onSuccess?: (nuevaReunion: Reunion) => void;
}

export default function AgendarSiguienteReunionModal({
  open,
  reunion,
  fechaSugerida,
  onClose,
  onSuccess,
}: Props) {
  const { t } = useTranslation<'translation'>();
  const { enqueueSnackbar } = useSnackbar();
  const { mutate: agendarSiguiente, isPending: isAgendando } = useAgendarSiguienteReunion();
  const { rows: usuarios = [] } = useUsuarios({ soloActivos: true });

  // Nota: useUsuarios retorna UsuarioFromHook que tiene nombreCompleto
  // TypeScript puede inferir el tipo incorrecto, así que hacemos cast explícito
  const staffList = (usuarios as unknown as UsuarioFromHook[]).filter(
    (u) => u.rol && !u.rol.includes('cliente') && !u.rol.includes('profesor'),
  );

  const [fechaInicio, setFechaInicio] = useState<Dayjs | null>(null);
  const [idStaff, setIdStaff] = useState<number | ''>('');
  const [canal, setCanal] = useState<ReunionCanal | ''>('');
  const { mutate: updateEstado, isPending: isUpdatingEstado } = useUpdateReunionEstado();
  const [estadoSel, setEstadoSel] = useState<string>('');

  // Inicializar valores cuando se abre el modal
  useEffect(() => {
    if (open && reunion) {
      // Fecha sugerida (7 días después de fechaFin o desde prop)
      const fechaSug = fechaSugerida
        ? dayjs(fechaSugerida).tz(TZ)
        : reunion.fechaFin
          ? dayjs(reunion.fechaFin).tz(TZ).add(7, 'days')
          : dayjs().tz(TZ).add(7, 'days');

      setFechaInicio(fechaSug);
      setIdStaff(reunion.idStaff || '');
      setCanal(reunion.canal || '');
      setEstadoSel(reunion.estado || '');
    } else if (!open) {
      // Resetear valores cuando se cierra el modal
      setFechaInicio(null);
      setIdStaff('');
      setCanal('');
      setEstadoSel('');
    }
  }, [open, reunion, fechaSugerida]);

  const handleAgendar = () => {
    if (!reunion || !fechaInicio) {
      enqueueSnackbar('Por favor, completa todos los campos requeridos', { variant: 'error' });
      return;
    }

    // Validar que idStaff sea un número válido si se proporciona
    if (idStaff && (isNaN(Number(idStaff)) || Number(idStaff) <= 0)) {
      enqueueSnackbar('Por favor, selecciona un staff válido', { variant: 'error' });
      return;
    }

    agendarSiguiente(
      {
        id: reunion.id,
        payload: {
          fechaInicio: fechaInicio.toISOString(),
          idStaff: idStaff ? Number(idStaff) : undefined,
          canal: canal || undefined,
        },
      },
      {
        onSuccess: (nuevaReunion) => {
          enqueueSnackbar('Siguiente reunión agendada correctamente', { variant: 'success' });
          onSuccess?.(nuevaReunion);
          onClose();
        },
        onError: (error: any) => {
          const mensaje =
            error?.response?.data?.message ||
            error?.message ||
            'Error al agendar la siguiente reunión';
          enqueueSnackbar(mensaje, { variant: 'error' });
        },
      },
    );
  };

  const servicioNombre = reunion ? getServicioNombre(reunion.servicioId) : '';

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Agendar siguiente reunión</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={3} sx={{ mt: 1 }}>
          {reunion && (
            <Alert severity="info">
              <Typography variant="body2">
                <strong>Servicio:</strong> {servicioNombre}
              </Typography>
              <Typography variant="body2" sx={{ mt: 0.5 }}>
                <strong>Alumno:</strong>{' '}
                {reunion.alumno
                  ? (reunion.alumno as any).nombreCompleto ||
                    `${reunion.alumno.nombre || ''} ${reunion.alumno.apellidos || ''}`.trim() ||
                    reunion.alumno.email ||
                    '—'
                  : '—'}
              </Typography>
            </Alert>
          )}

          <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="es">
            <DateTimePicker
              label="Fecha y hora de inicio"
              value={fechaInicio}
              onChange={(newValue) => setFechaInicio(newValue)}
              minDate={dayjs().tz(TZ)}
              slotProps={{
                textField: {
                  fullWidth: true,
                  required: true,
                  error: !fechaInicio,
                  helperText: !fechaInicio ? 'La fecha es requerida' : '',
                },
              }}
            />
          </LocalizationProvider>

          {/* Selector de estado de reunión y acción para actualizarlo */}
          <FormControl fullWidth>
            <InputLabel>Estado reunión</InputLabel>
            <Select
              value={estadoSel}
              label="Estado reunión"
              onChange={(e) => setEstadoSel(e.target.value as string)}
            >
              {REUNION_ESTADO_VALUES.map((e) => (
                <MenuItem key={e} value={e}>
                  {getEstadoDisplayLabel(e)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl fullWidth required>
            <InputLabel>Staff</InputLabel>
            <Select
              value={idStaff}
              label="Staff"
              onChange={(e) => setIdStaff(e.target.value as number | '')}
              error={!idStaff}
            >
              {staffList.map((usuario) => {
                const u = usuario as UsuarioFromHook;
                return (
                  <MenuItem key={u.id} value={u.id}>
                    {u.nombreCompleto || u.email}
                  </MenuItem>
                );
              })}
            </Select>
          </FormControl>

          <FormControl fullWidth>
            <InputLabel>Canal</InputLabel>
            <Select
              value={canal}
              label="Canal"
              onChange={(e) => setCanal(e.target.value as ReunionCanal | '')}
            >
              {REUNION_CANAL_VALUES.map((c) => (
                <MenuItem key={c} value={c}>
                  {c}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={isAgendando || isUpdatingEstado} variant="outlined">
          {t('common.cancel', 'Cancelar')}
        </Button>
        <Button
          onClick={() => {
            if (!reunion || !estadoSel || estadoSel === reunion.estado) return;
            updateEstado(
              { id: reunion.id, estado: estadoSel as any },
              {
                onSuccess: () => {
                  enqueueSnackbar('Estado de la reunión actualizado', { variant: 'success' });
                },
                onError: (err: any) => {
                  const mensaje =
                    err?.response?.data?.message || err?.message || 'Error al actualizar estado';
                  enqueueSnackbar(mensaje, { variant: 'error' });
                },
              },
            );
          }}
          disabled={isAgendando || isUpdatingEstado || !estadoSel || estadoSel === (reunion?.estado || '')}
          variant="outlined"
          startIcon={isUpdatingEstado ? <CircularProgress size={18} /> : null}
          sx={{ mr: 1, color: '#21a598', borderColor: '#21a598' }}
        >
          Cambiar estado reunión
        </Button>
        <Button
          onClick={handleAgendar}
          disabled={isAgendando || !fechaInicio || !idStaff}
          variant="contained"
          startIcon={isAgendando ? <CircularProgress size={20} /> : null}
          sx={{
            backgroundColor: '#21a598',
            '&:hover': { backgroundColor: '#1a8578' },
          }}
        >
          {isAgendando ? 'Agendando...' : 'Agendar siguiente reunión'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
